import { Link, Navigate, useParams } from "react-router";
import { ArrowRight, CheckCircle, ChevronRight, Shield } from "lucide-react";
import { getIndustryTopic } from "../data/industryTopics";
import { CctvHighlightBlock } from "../components/CctvHighlightBlock";
import { WebcamHighlightBlock } from "../components/WebcamHighlightBlock";
import { MarketingBottomCta } from "../components/MarketingBottomCta";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";

const SITE_ORIGIN = "https://www.deskreplay.com";

export function IndustryTopicPage() {
  const { slug } = useParams();
  const topic = getIndustryTopic(slug);

  if (!topic) {
    return <Navigate to="/industries" replace />;
  }

  const cta = topic.marketingCta;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE_ORIGIN}/industries` },
      { "@type": "ListItem", position: 3, name: topic.pageTitle, item: `${SITE_ORIGIN}/industries/${topic.slug}` },
    ],
  };

  return (
    <div className="bg-slate-950 min-h-screen">
      <Seo
        title={topic.pageTitle}
        description={topic.metaDescription}
        canonicalPath={`/industries/${topic.slug}`}
      />
      <JsonLd schema={breadcrumbSchema} id="breadcrumb" />

      <section className="relative pt-36 pb-16 overflow-hidden border-b border-teal-500/10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(13, 148, 136, 0.13) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-xs text-on-dark-muted mb-8">
            <Link to="/" className="hover:text-teal-400 transition-colors">
              Home
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/industries" className="hover:text-teal-400 transition-colors">
              Industries
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-slate-400">{topic.label}</span>
          </nav>

          <Link
            to="/industries"
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm transition-colors"
          >
            <ChevronRight size={16} className="rotate-180" />
            All industries overview
          </Link>

          <h1
            className="text-slate-100 mt-8 mb-4 max-w-4xl"
            style={{
              fontSize: "clamp(1.85rem, 4vw, 2.75rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: "1.15",
            }}
          >
            {topic.headline}
          </h1>
          <p className="text-on-dark-muted text-lg max-w-3xl leading-relaxed">{topic.description}</p>
        </div>
      </section>

      {topic.variant === "featured" && topic.image ? (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`rounded-2xl border overflow-hidden ${
                topic.accentColor === "teal" ? "border-teal-500/15" : "border-emerald-500/15"
              }`}
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto min-h-[280px] overflow-hidden">
                  <img src={topic.image} alt={topic.imageAlt ?? topic.label} className="w-full h-full object-cover opacity-40" />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: "linear-gradient(to left, transparent, var(--color-slate-900) 90%)",
                    }}
                  />
                  <div className="absolute top-6 left-6">
                    <div className={`w-12 h-12 rounded-xl ${topic.iconBg} flex items-center justify-center shadow-lg`}>
                      <topic.icon size={22} className="text-white" />
                    </div>
                  </div>
                </div>

                <div className="p-10 lg:p-14">
                  <span
                    className={`text-xs uppercase tracking-widest mb-6 block ${
                      topic.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    {topic.label}
                  </span>

                  <div>
                    <p className="text-on-dark-muted text-xs uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
                      Key Risk Scenarios
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-8">
                      {topic.keyRisks.map((risk) => (
                        <div key={risk} className="flex items-start gap-2">
                          <CheckCircle
                            size={14}
                            className={`mt-0.5 shrink-0 ${
                              topic.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                            }`}
                          />
                          <span className="text-on-dark-muted text-xs">{risk}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link
                    to="/solutions"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm transition-all duration-200 hover:shadow-lg ${
                      topic.accentColor === "teal"
                        ? "bg-teal-600 hover:bg-teal-500 hover:shadow-teal-500/25"
                        : "bg-emerald-600 hover:bg-emerald-500 hover:shadow-emerald-500/25"
                    }`}
                    style={{ fontWeight: 600 }}
                  >
                    <Shield size={15} />
                    See {topic.label} solutions
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className={`rounded-2xl border p-10 lg:p-14 ${
                topic.accentColor === "teal" ? "border-teal-500/15" : "border-emerald-500/15"
              }`}
              style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-14 h-14 rounded-xl ${topic.iconBg} flex items-center justify-center shadow-lg`}>
                  <topic.icon size={26} className="text-white" />
                </div>
                <span
                  className={`text-xs uppercase tracking-widest ${
                    topic.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                  }`}
                  style={{ fontWeight: 600 }}
                >
                  {topic.label}
                </span>
              </div>

              <p className="text-on-dark-muted text-sm leading-relaxed mb-10">{topic.description}</p>

              <p className="text-on-dark-muted text-xs uppercase tracking-widest mb-4" style={{ fontWeight: 600 }}>
                Key Risk Scenarios
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-10">
                {topic.keyRisks.map((risk) => (
                  <div key={risk} className="flex items-start gap-2">
                    <CheckCircle
                      size={14}
                      className={`mt-0.5 shrink-0 ${
                        topic.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                      }`}
                    />
                    <span className="text-on-dark-muted text-sm">{risk}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/solutions"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm transition-all duration-200 hover:shadow-lg ${
                  topic.accentColor === "teal"
                    ? "bg-teal-600 hover:bg-teal-500 hover:shadow-teal-500/25"
                    : "bg-emerald-600 hover:bg-emerald-500 hover:shadow-emerald-500/25"
                }`}
                style={{ fontWeight: 600 }}
              >
                <Shield size={15} />
                Explore matching solutions
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-6">
          <CctvHighlightBlock {...topic.cctvHighlight} />
          {topic.webcamHighlight ? <WebcamHighlightBlock {...topic.webcamHighlight} /> : null}
        </div>
      </section>

      <MarketingBottomCta titleBefore={cta.titleBefore} titleAccent={cta.titleAccent} description={cta.description} />
    </div>
  );
}
